import React, { createContext, useContext, useState, useEffect, useMemo } from 'react'
import { auth } from '@/lib/picasso-firebase';
import { onAuthStateChanged } from 'firebase/auth';

type PicassoAuthContextType = {
  isAdmin: boolean;
  userEmail: string | null;
  loading: boolean;
};

const PicassoAuthContext = createContext<PicassoAuthContextType>({
  isAdmin: false,
  userEmail: null,
  loading: true,
});

export const PicassoAuthProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(false);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const API_BASE_URL = import.meta.env.VITE_BASE_URL;

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUserEmail(user.email);
        try {
          const token = await user.getIdToken();
          const response = await fetch(`${API_BASE_URL}/picasso/auth/me`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
          });
          if (response.ok) {
            const userData = await response.json();
            setIsAdmin(userData.role === 'admin');
          }
        } catch (error) {
          console.error('Failed to fetch user role:', error);
          setIsAdmin(false);
        }
      } else {
        setUserEmail(null);
        setIsAdmin(false);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [API_BASE_URL]);

  const contextValue = useMemo(() => ({
    isAdmin,
    userEmail,
    loading
  }), [isAdmin, userEmail, loading]);

  return (
    <PicassoAuthContext.Provider value={contextValue}>
      {children}
    </PicassoAuthContext.Provider>
  );
};

export const usePicassoAuth = () => useContext(PicassoAuthContext);